import type { OutgoingImagePolicy } from '@functions/schemas/database';

export const OUTGOING_IMAGE_BYTES_PER_MB = 1024 * 1024;
export const OUTGOING_IMAGE_MAX_BUDGET_MB = 100;

type EnabledOutgoingImagePolicy = Extract<OutgoingImagePolicy, { enabled: true }>;
export type OutgoingImagePolicyDraft = { enabled: boolean } & Partial<Omit<EnabledOutgoingImagePolicy, 'enabled'>>;

export const DEFAULT_OUTGOING_IMAGE_POLICY: EnabledOutgoingImagePolicy = {
  enabled: true,
  output_format: 'jpeg',
  quality: 0.85,
  max_dimension: 2048,
  max_image_size_mb: 2,
  max_total_size_mb: 10,
};

/** Binary MB, rounded down so the configured limit is never exceeded. */
export function outgoingImageBudgetBytes(value: number): number {
  const bytes = Math.floor(value * OUTGOING_IMAGE_BYTES_PER_MB);
  if (!Number.isFinite(value) || value <= 0 || value > OUTGOING_IMAGE_MAX_BUDGET_MB
    || !Number.isSafeInteger(bytes) || bytes < 1) {
    throw new Error('Image size limits must be between one byte and 100 MB');
  }
  return bytes;
}

/** Validate an enabled draft; disabled drafts may contain unfinished form fields. */
export function normalizeOutgoingImagePolicy(
  policy: OutgoingImagePolicyDraft | null | undefined,
): OutgoingImagePolicy | null | undefined {
  if (policy === undefined) return undefined;
  if (policy === null) return null;
  if (policy.enabled === false) return { enabled: false };
  if (policy.enabled !== true || (policy.output_format !== 'jpeg' && policy.output_format !== 'webp')
    || typeof policy.quality !== 'number' || !Number.isFinite(policy.quality)
    || policy.quality < 0.1 || policy.quality > 1
    || typeof policy.max_dimension !== 'number' || !Number.isInteger(policy.max_dimension)
    || policy.max_dimension < 512 || policy.max_dimension > 8192
    || typeof policy.max_image_size_mb !== 'number' || typeof policy.max_total_size_mb !== 'number') {
    throw new Error('Complete the outgoing image settings with valid values');
  }
  outgoingImageBudgetBytes(policy.max_image_size_mb);
  outgoingImageBudgetBytes(policy.max_total_size_mb);
  return {
    enabled: true,
    output_format: policy.output_format,
    quality: policy.quality,
    max_dimension: policy.max_dimension,
    max_image_size_mb: policy.max_image_size_mb,
    max_total_size_mb: policy.max_total_size_mb,
  };
}

export function isOutgoingImagePolicyValid(policy: OutgoingImagePolicyDraft | null | undefined): boolean {
  try {
    normalizeOutgoingImagePolicy(policy);
    return true;
  } catch {
    return false;
  }
}
